import React from 'react';

import { NarrativeState } from '../types/narrative';
import {
  formatBPlotVariantTable,
  formatNode2VariantTable,
  getBPlotVariants,
  getNode2Variants,
  shouldShowVariantTable,
} from '../utils/variantTableHelpers';

interface VariantTableProps {
  nodeId: string;
  narrativeState: NarrativeState;
}

export const VariantTable = React.memo(function VariantTable({
  nodeId,
  narrativeState,
}: VariantTableProps) {
  if (!shouldShowVariantTable(nodeId)) {
    return null;
  }

  let tableContent: string = '';

  if (nodeId === 'S1_Player_Declaration') {
    const variants = getNode2Variants(narrativeState);
    tableContent = formatNode2VariantTable(variants);
  } else {
    const variants = getBPlotVariants(nodeId, narrativeState);
    tableContent = formatBPlotVariantTable(variants);
  }

  // Convert markdown table to JSX
  const rows = tableContent.split('\n').filter((row) => row.trim());

  return (
    <div className="mb-4 text-xs">
      {/* Glassy card container */}
      <div className="bg-black bg-opacity-30 backdrop-blur-sm border border-gray-700 rounded-lg p-4">
        {/* Header text */}
        <p className="text-gray-300 mb-2">
          The choices leading to this node can cause any of the following variant opening lines:
        </p>

        {/* Top horizontal line */}
        <hr className="border-gray-600 mb-3" />

        {/* Table */}
        <table className="w-full border-collapse">
          <tbody>
            {rows.map((row, rowIndex) => {
              const cells = row.split('|').filter((cell) => cell.trim());
              const isHeader = rowIndex === 0;
              const isSeparator = rowIndex === 1;

              if (isSeparator) return null;

              return (
                <tr key={rowIndex} className={isHeader ? 'border-b border-gray-600' : ''}>
                  {cells.map((cell, cellIndex) => {
                    const CellTag = isHeader ? 'th' : 'td';

                    // Parse the cell content for bold and styled text
                    const parsedContent = parseTableCell(cell.trim());

                    return (
                      <CellTag
                        key={cellIndex}
                        className={`
                          px-2 py-1 text-left
                          ${isHeader ? 'font-semibold text-gray-300' : 'text-gray-400'}
                          ${cellIndex === 0 ? 'font-medium' : ''}
                        `}
                      >
                        {parsedContent}
                      </CellTag>
                    );
                  })}
                </tr>
              );
            })}
          </tbody>
        </table>

        {/* Bottom horizontal line */}
        <hr className="border-gray-600 mt-3 mb-2" />

        {/* Reminder text */}
        <p className="text-yellow-400 text-[11px] italic">
          ⚠️ Reminder: the below text/choices could flow from -any- of the above.
        </p>
      </div>
    </div>
  );
});

function parseTableCell(content: string): React.ReactNode {
  if (content === '-') {
    return <span className="text-gray-600">-</span>;
  }

  // Parse bold text and styled spans
  const parts: React.ReactNode[] = [];
  let currentIndex = 0;

  // Handle bold text
  const boldRegex = /\*\*([^*]+)\*\*/g;
  let boldMatch;

  const tempContent = content.replace(/<br\/>/g, '|||BR|||');

  while ((boldMatch = boldRegex.exec(tempContent)) !== null) {
    // Add text before the bold part
    if (boldMatch.index > currentIndex) {
      const textBefore = tempContent.substring(currentIndex, boldMatch.index);
      parts.push(parseNonBoldText(textBefore));
    }

    // Add the bold text
    parts.push(
      <span key={`bold-${boldMatch.index}`} className="font-bold text-white">
        {boldMatch[1]}
      </span>
    );

    currentIndex = boldMatch.index + boldMatch[0].length;
  }

  // Add any remaining text
  if (currentIndex < tempContent.length) {
    parts.push(parseNonBoldText(tempContent.substring(currentIndex)));
  }

  return parts.length > 0 ? <>{parts}</> : parseNonBoldText(tempContent);
}

function parseNonBoldText(text: string): React.ReactNode {
  // Handle line breaks and styled spans
  const parts = text.split('|||BR|||');

  return parts.map((part, index) => {
    // Check for styled span
    const spanMatch = part.match(/<span[^>]*style="color:\s*#e5e7eb"[^>]*>([^<]+)<\/span>/);

    if (spanMatch) {
      const beforeSpan = part.substring(0, spanMatch.index || 0);
      const spanContent = spanMatch[1];
      const afterSpan = part.substring((spanMatch.index || 0) + spanMatch[0].length);

      return (
        <React.Fragment key={`part-${index}`}>
          {beforeSpan && <span>{beforeSpan}</span>}
          <span className="text-gray-200">{spanContent}</span>
          {afterSpan && <span>{afterSpan}</span>}
          {index < parts.length - 1 && <br />}
        </React.Fragment>
      );
    }

    return (
      <React.Fragment key={`part-${index}`}>
        {part}
        {index < parts.length - 1 && <br />}
      </React.Fragment>
    );
  });
}
